import {
  bytesToIso11784Value,
  Iso11784Fields,
  parseIso11784Data,
} from "lib/eid/iso11784";

export const BAC_TAG_DATA_ID = 0x25;

export enum GallagherTagType {
  FDX = 0,
  HDX = 1,
  FDX_INDUSTRIAL = 2,
  HDX_INDUSTRIAL = 3,
}

export type GallagherRawTagData = Iso11784Fields;

type TagDataId = 0x25;

export interface GallagherAnimalTag {
  tagDataId: TagDataId;
  tagType: GallagherTagType;
  tagData: GallagherRawTagData;
}

export const decodeRawTagData = (tagData: Uint8Array): GallagherRawTagData => {
  const fields = parseIso11784Data(bytesToIso11784Value(tagData));

  if (fields.dataBlockFlag) {
    // ISO 11784/11785 chains additional data blocks after the 64 bit code when this flag is
    // set, but this 8 byte payload has no room for them - the flag being set means the tag
    // is reporting data we can't access, so treat it as unparseable rather than silently
    // ignoring the extra blocks.
    throw new Error(
      "Tag reports an additional ISO 11784/11785 data block, which this payload has no room for",
    );
  }

  return fields;
};

const base64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(base64);
  return Uint8Array.from(binary, char => char.charCodeAt(0));
};

export const parseBinaryAgFrame = (base64Str: string): GallagherAnimalTag => {
  const bytes = base64ToBytes(base64Str);

  if (bytes.length !== 10) {
    throw new Error(`Expected a 10 byte payload, got ${bytes.length}`);
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tagDataId = view.getUint8(0);

  if (tagDataId !== BAC_TAG_DATA_ID) {
    throw new Error(
      `Expected tagDataId 0x${BAC_TAG_DATA_ID.toString(16)}, got 0x${tagDataId.toString(16)}`,
    );
  }

  const tagType = view.getUint8(1);

  if (!(tagType in GallagherTagType)) {
    throw new Error(`Unknown tagType ${tagType}`);
  }

  return {
    tagDataId,
    tagType,
    tagData: decodeRawTagData(bytes.slice(2, 10)),
  };
};
